// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        countries: getCurrentCountries(state),
    };
};

const mapDispatchToProps = {
    loadCurrentCountries,
};

const capitalize = css`
    text-transform: lowercase;
    &:first-line {
        text-transform: capitalize;
    }
    font-style: normal !important;
`;
export interface AddressInfoDisplayStyles {
    wrapper?: InjectableCss;
    customerNameText?: TypographyProps;
    firstNameText?: TypographyProps;
    lastNameText?: TypographyProps;
    attentionText?: TypographyProps;
    companyNameText?: TypographyProps;
    address1Text?: TypographyProps;
    address2Text?: TypographyProps;
    address3Text?: TypographyProps;
    address4Text?: TypographyProps;
    cityStatePostalCodeText?: TypographyProps;
    cityText?: TypographyProps;
    stateText?: TypographyProps;
    postalCodeText?: TypographyProps;
    countryText?: TypographyProps;
    phoneText?: TypographyProps;
    emailText?: InjectableCss;
    faxText?: TypographyProps;
    phoneLink?: LinkPresentationProps;
    emailLink?: LinkPresentationProps;
}

export const addressInfoDisplayStyles: AddressInfoDisplayStyles = {
    wrapper: {
        css: css`
            font-style: normal;
            margin-top: 8px;

            * {
                line-height: ${fontConfig.lineHeight.regular};
            }
            p {
                font-style: normal !important;
            }
        `,
    },
    customerNameText: {
        as: "p",
        css: css`
            margin-bottom: 8px;
        `,
    },
    firstNameText: {
        css: css`
            ${capitalize}
        `,
    },
    lastNameText: {
        css: css`
            ${capitalize}
        `,
    },
    attentionText: {
        as: "p",
        css: css`
            ${capitalize}
            margin-bottom: 8px;
        `,
    },
    companyNameText: {
        as: "p",
        weight: fontConfig.weight.bold,
        css: css`
            margin-bottom: 8px;
            ${capitalize}
        `,
    },
    address1Text: {
        as: "p",
        css: css`
            ${capitalize}
        `,
    },
    address2Text: {
        as: "p",
        css: css`
            ${capitalize}
        `,
    },
    address3Text: {
        as: "p",
        css: css`
            ${capitalize}
        `,
    },
    address4Text: {
        as: "p",
        css: css`
            ${capitalize}
        `,
    },
    cityStatePostalCodeText: {
        as: "p",
        css: css`
            ${capitalize}
        `,
    },
    postalCodeText: {
        css: css`
            text-transform: uppercase;
        `,
    },
    stateText: {
        css: css`
            && {
                text-transform: uppercase;
            }
        `,
    },
    countryText: {
        as: "p",
    },
    phoneText: {
        as: "p",
    },
    phoneLink: {
        css: css`
            & > ${TypographyStyle} {
                font-weight: ${fontConfig.weight.regular};
                color: ${getColor("text.main")};
            }
        `,
    },
    emailLink: {
        css: css`
            margin-top: 8px;
            & > ${TypographyStyle} {
                font-weight: ${fontConfig.weight.semiBold};
                line-height: ${fontConfig.lineHeight.regular};
                letter-spacing: 0;
            }
        `,
    },
    faxText: {
        as: "p",
    },
};

interface OwnProps {
    firstName?: string;
    lastName?: string;
    companyName?: string;
    customerNumber?: string;
    attention?: string;
    address1: string;
    address2?: string;
    address3?: string;
    address4?: string;
    city: string;
    state?: string;
    postalCode: string;
    country?: string;
    phone?: string;
    fax?: string;
    email?: string;
    extendedStyles?: AddressInfoDisplayStyles;
    horizontalDisplay?: boolean;
    countryId?: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const StyledAddress = getStyledWrapper("address");

const AddressInfoDisplay: React.FunctionComponent<Props> = function Address(props: Props) {
    const [styles] = React.useState(() => mergeToNew(addressInfoDisplayStyles, props.extendedStyles));
    const [countryFromId, setCountryFromId] = React.useState("");
    const [countriesLoaded, setCountriesLoaded] = React.useState(false);

    React.useEffect(() => {
        if (!props.country) {
            if (!props.countries) {
                props.loadCurrentCountries();
            } else if (props?.countryId && props.countries) {
                const c = props?.countries?.find(c => c.id === (props as any).countryId) || "";
                if (c) {
                    setCountryFromId(c.abbreviation);
                }
            }
        }
    }, [props.countries]);

    return (
        <StyledAddress {...styles.wrapper} data-test-selector="addressInfoDisplay">
            {!props.horizontalDisplay && (
                <>
                    {props.companyName && (
                        <Typography {...styles.companyNameText} data-test-selector="addressInfoDisplay_companyName">
                            {props.companyName}{" "}
                            <span className="cust-number__name">
                                {props.customerNumber ? `#${props.customerNumber}` : ""}
                            </span>
                        </Typography>
                    )}
                    <FullName
                        firstName={props.firstName}
                        lastName={props.lastName}
                        styles={styles}
                        data-test-selector="addressInfoDisplay_fulName"
                    />
                    {props.attention && (
                        <Typography {...styles.attentionText} data-test-selector="addressInfoDisplay_attention">
                            {translate("Attention:")} {props.attention}
                        </Typography>
                    )}
                    {props.address1 && (
                        <Typography {...styles.address1Text} data-test-selector="addressInfoDisplay_address1">
                            {`${props.address1} `}
                        </Typography>
                    )}
                    {props.address2 && (
                        <Typography {...styles.address2Text} data-test-selector="addressInfoDisplay_address2">
                            {`${props.address2} `}
                        </Typography>
                    )}
                    {props.address3 && (
                        <Typography {...styles.address3Text} data-test-selector="addressInfoDisplay_address3">
                            {`${props.address3} `}
                        </Typography>
                    )}
                    {props.address4 && (
                        <Typography {...styles.address4Text} data-test-selector="addressInfoDisplay_address4">
                            {`${props.address4} `}
                        </Typography>
                    )}
                    <CityStatePostalCode
                        city={props.city}
                        state={props.state}
                        postalCode={props.postalCode}
                        styles={styles}
                    />
                    {props.country && (
                        <Typography {...styles.countryText} data-test-selector="addressInfoDisplay_country">
                            {`${props.country} `}
                        </Typography>
                    )}
                    {countryFromId && (
                        <Typography {...styles.countryText} data-test-selector="addressInfoDisplay_country">
                            {`${countryFromId} `}
                        </Typography>
                    )}
                    {(props.phone || props.fax || props.email) && (
                        <StyledWrapper
                            css={css`
                                margin-top: 8px;
                            `}
                        >
                            {props.phone && (
                                <Typography {...styles.phoneText} data-test-selector="addressInfoDisplay_phoneNumber">
                                    <Link
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href={`tel:${props.phone}`}
                                        data-test-selector="addressInfoDisplay_phoneNumber"
                                        aria-label={translate("Tel: {0}", props.phone)}
                                        {...styles.phoneLink}
                                    >
                                        {props.phone}
                                    </Link>
                                </Typography>
                            )}
                            {props.fax && (
                                <Typography {...styles.faxText} data-test-selector="addressInfoDisplay_fax">
                                    Fax: {props.fax}
                                </Typography>
                            )}
                            {props.email && (
                                <Link
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={`mailto:${props.email}`}
                                    {...styles.emailLink}
                                    data-test-selector="addressInfoDisplay_emailAddress"
                                    aria-label={translate("Email: {0}", props.email)}
                                >
                                    {props.email}
                                </Link>
                            )}
                        </StyledWrapper>
                    )}
                </>
            )}
            {props.horizontalDisplay && (
                <StyledWrapper
                    css={css`
                        display: flex;
                    `}
                >
                    <Typography>
                        {props.address1} {props.address2}, &nbsp;
                    </Typography>
                    <Typography>
                        <CityStatePostalCode
                            city={props.city}
                            state={props.state}
                            postalCode={props.postalCode}
                            styles={styles}
                        />
                    </Typography>
                    {props.country && (
                        <Typography aria-label={translate("country")}>
                            {`, `}&nbsp;{props.country}
                        </Typography>
                    )}
                    {countryFromId && (
                        <Typography aria-label={translate("country")}>
                            {`, `}&nbsp;{countryFromId}
                        </Typography>
                    )}
                    &nbsp;
                    <Typography aria-label={translate("phone number")}>
                        {`Ph: `}&nbsp;{props.phone}
                    </Typography>
                </StyledWrapper>
            )}
        </StyledAddress>
    );
};

interface FullNameProps {
    firstName?: string;
    lastName?: string;
    styles: AddressInfoDisplayStyles;
}

const FullName = ({ firstName, lastName, styles }: FullNameProps) => {
    const fullName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
    if (!fullName) {
        return null;
    }
    return (
        <Typography {...styles.customerNameText}>
            {firstName && (
                <Typography {...styles.firstNameText} data-test-selector="addressInfoDisplay_firstName">
                    {`${firstName} `}
                </Typography>
            )}
            {lastName && (
                <Typography {...styles.lastNameText} data-test-selector="addressInfoDisplay_lastName">
                    {lastName}
                </Typography>
            )}
        </Typography>
    );
};

interface CityStatePostalCodeProps {
    city: string;
    state?: string;
    postalCode: string;
    styles: AddressInfoDisplayStyles;
}

const CityStatePostalCode = ({ city, state, postalCode, styles }: CityStatePostalCodeProps) => {
    return (
        <Typography {...styles.cityStatePostalCodeText}>
            <Typography {...styles.cityText} data-test-selector="addressInfoDisplay_city">
                {city}
            </Typography>
            {state && (
                <>
                    {", "}
                    <Typography {...styles.stateText} data-test-selector="addressInfoDisplay_state">
                        {state}
                    </Typography>
                </>
            )}
            {postalCode && (
                <>
                    {"  "}
                    <Typography {...styles.postalCodeText} data-test-selector="addressInfoDisplay_postalCode">
                        {postalCode}
                    </Typography>
                </>
            )}
        </Typography>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AddressInfoDisplay);
